import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@vercel/analytics/react';
import Hero from './components/Hero';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Experience from './components/Experience';
import Contact from './components/Contact';
import Resume from './components/Resume';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import NotFound from './components/NotFound';
import Timeline from './components/Timeline';
import Hobbies from './components/Hobbies';
import Credentials from './components/Credentials';

const App = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [, setCurrentPath] = useState('/');
  const [showNotFound, setShowNotFound] = useState(false);
  const [showHobbies, setShowHobbies] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(prefersDark);

    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }

    // Obtener la ruta actual
    const path = window.location.pathname;
    setCurrentPath(path);

    // Si la URL contiene #hobbies, mostrar la sección de hobbies
    if (window.location.hash === '#hobbies') {
      setShowHobbies(true);
    }

    const knownPaths = ['/', '/mi-portafolio', '/mi-portafolio/'];
    if (!knownPaths.includes(path)) {
      setShowNotFound(true);
    }

    // Escuchar cambios en el hash
    const handleHashChange = () => {
      if (window.location.hash === '#hobbies') {
        setShowHobbies(true);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [i18n]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleHobbiesClose = () => {
    setShowHobbies(false);
    window.history.pushState(null, null, window.location.pathname);
  };

  const openHobbies = () => {
    setShowHobbies(true);
    window.history.pushState(null, null, `${window.location.pathname}#hobbies`);
  };

  if (showNotFound) {
    return (
        <>
          <NotFound darkMode={darkMode} />
          <Analytics />
        </>
    );
  }

  return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} onOpenHobbies={openHobbies} />

        <main className="container mx-auto px-4 py-8">
          <Hero darkMode={darkMode} />

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Projects darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Skills darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Timeline darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Experience darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Credentials darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Resume darkMode={darkMode} />
          </motion.div>

          <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="my-16"
          >
            <Contact darkMode={darkMode} />
          </motion.div>

          {/* Botón flotante para acceder a la sección de hobbies */}
          <div className="fixed bottom-8 right-8">
            <button
                onClick={openHobbies}
                className={`p-4 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105 ${
                    darkMode
                        ? 'bg-cyan-600 text-white hover:bg-cyan-500'
                        : 'bg-cyan-500 text-white hover:bg-cyan-600'
                }`}
                aria-label="Ver mis intereses personales"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>
        </main>

        <Footer darkMode={darkMode} onOpenHobbies={openHobbies} />

        {/* Modal de Hobbies */}
        {showHobbies && (
            <Hobbies darkMode={darkMode} onClose={handleHobbiesClose} />
        )}

        {/* Vercel Analytics */}
        <Analytics />
      </div>
  );
};

export default App;